/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { matchPath } from 'react-router';

/**
 * Routes rendered inside `AgencyDashboardLayout` — the agency workspace, not
 * the agency *picker* at `/app/agencies`, which has no sidebar of its own.
 */
const AGENCY_SHELL_PATTERN = '/app/agencies/:agency_id/*';

/**
 * Whether the current route draws its own chrome and wants no global bar.
 *
 * The desktop agency shell absorbed everything the global top bar used to
 * carry (brand, feedback, user menu) into the sidebar, so keeping the bar
 * would only stack a duplicate strip above it and cost 48px of page height.
 *
 * Mobile reached the same place from the other direction: it has no sidebar,
 * so it grew a top bar of its own inside the shell -- one that knows which
 * agency you are in, and that can hand its height to a flex column instead of
 * having every page subtract `--header-height` by hand. Two bars, one of them
 * showing the wrong brand row, is what this keeps from happening.
 *
 * The parameter stays even though both answers now agree: the *reason* they
 * agree is different on each surface, and a caller that stops passing it
 * would silently start asking a question this function no longer answers.
 *
 * @param pathname - Current location pathname.
 * @param _isMobile - Whether the viewport is at or below the mobile breakpoint.
 * @returns `true` when the global top bar must not render.
 */
export function usesSidebarChrome(pathname: string, _isMobile: boolean): boolean {
  return Boolean(matchPath(AGENCY_SHELL_PATTERN, pathname));
}

/**
 * Routes that open on top of a list: one record, filling the screen.
 *
 * The `*` in the shell pattern makes these look like any other agency route,
 * so they are matched by shape instead — a segment after the module's own.
 * `properties` is a list and `properties/AP-2841` is a thing, and only the
 * second one is a detail screen.
 */
const DETAIL_ROUTE_PATTERNS = [
  '/app/agencies/:agency_id/properties/:id/*',
  '/app/agencies/:agency_id/clients/:id/*',
  '/app/agencies/:agency_id/deals/:id/*',
  '/app/agencies/:agency_id/visits/:id/*',
];

/**
 * Whether this route draws its own chrome at both ends of the phone.
 *
 * A detail screen is about one record, and both of the shell's bars are about
 * everything else. At the bottom it carries its own actions — "Enviar" and
 * "Agendar visita" on a listing, "Registrar contato" and "Andamento" on an
 * attendance — and stacking the navigation under them puts the thumb over the
 * tab bar when it was reaching for the action. At the top the shell's bar
 * spends 56px on the product's name and your own avatar, neither of which
 * changes between screens, while the record's name and its actions sit down
 * in the scrolling content. The screen's own 52px header says which record
 * you are in and keeps the way back and the actions fixed.
 *
 * @param pathname - Current location pathname.
 * @returns `true` when the shell's own bars must not render.
 */
export function isDetailRoute(pathname: string): boolean {
  return DETAIL_ROUTE_PATTERNS.some((pattern) => Boolean(matchPath(pattern, pathname)));
}
